<script setup>
import BottomSheet from '@/components/modal/BottomSheet.vue'
import { onUnmounted, ref } from 'vue'
import NavButton from '@/components/NavButton.vue'
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings'
import NotificationsService from '@/services/NotificationsService.js'
import { Settings } from '@/store/index.js'
import { App } from '@capacitor/app'

const bottomSheetRef = ref(null)

const isDenied = ref(null)
const requesting = ref(false)

let resolve = null
let resumeListener = null

async function open () {
  const perms = await NotificationsService.checkPermissions()
  isDenied.value = perms === 'denied'

  await bottomSheetRef.value.open()

  return new Promise((r) => {
    resolve = r
  })
}

async function checkAndEnable () {
  try {
    const perms = await NotificationsService.checkPermissions()

    if (perms === 'granted') {
      await Settings.enableNotifications(true)
      close()
    }
  } catch (error) {
    console.error('Error enabling notifications:', error)
  } finally {
    requesting.value = false
  }
}

async function openSettings () {
  try {
    requesting.value = true
    // if the permission is denied, we need to check if the user has enabled it after visiting the settings
    if (resumeListener) resumeListener.remove()
    resumeListener = await App.addListener('resume', () => checkAndEnable())

    await NativeSettings.open({
      optionAndroid: AndroidSettings.ApplicationDetails,
      optionIOS: IOSSettings.App
    })
  } catch (error) {
    console.error('Error opening settings:', error)
  }
}

async function requestNotifications () {
  requesting.value = true

  try {
    await Settings.enableNotifications(true)
  } catch (error) {
    console.error('Error requesting notifications permission:', error)
  } finally {
    requesting.value = false
    close()
  }
}

function close () {
  return bottomSheetRef.value.close()
}

function onHide() {
  // Clean up listener when component is unmounted
  if (resumeListener) {
    resumeListener.remove()
    resumeListener = null
  }

  if (resolve) {
    resolve()
    resolve = null
  }
}

onUnmounted(() => {
  if (resumeListener) {
    resumeListener.remove()
    resumeListener = null
  }
})

defineExpose({
  open
})
</script>

<template>
    <bottom-sheet ref="bottomSheetRef" @hide="onHide" v-bind="$attrs">
        <h1 class="px-6 text-center" v-html="$t('enableNotifications.title')"></h1>
        <div class="px-6 pt-6 text-center" v-html="$t('enableNotifications.description')"></div>
        <template v-if="isDenied">
            <div class="px-6 pt-6 text-center" v-html="$t('enableNotifications.systemSettings.description')"></div>
            <div class="p-6">
                <nav-button
                        :label="$t('enableNotifications.systemSettings.button')"
                        @click="openSettings()"
                        :busy="requesting"
                />
            </div>
        </template>

        <template v-else>
            <div class="p-6">
                <nav-button
                        :label="$t('enableNotifications.requestNotifications.button')"
                        @click="requestNotifications()"
                        :busy="requesting"
                />
            </div>
        </template>
    </bottom-sheet>
</template>

<style scoped lang="scss">

</style>
